import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { RouterSelectors } from '@next/core-lib/router-store';
import { UserActions, UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RouterNavigationAction, ROUTER_NAVIGATION } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { FLOCK_VARIANT, FlockVariant } from 'src/app/flock/models/flock.model';
import * as flockActions from 'src/app/flock/store/actions/flock.actions';

@Injectable()
export class LayerEffects {
  private readonly currentUrl$ = this.store.pipe(select(RouterSelectors.getUrl)).pipe(
    map((url) => url || `${globalThis.location.pathname}${globalThis.location.search}`),
    distinctUntilChanged(),
  );
  private readonly selectedCustomer$ = this.store.select(UserSelectors.selectCustomer);
  private readonly selectedLocation$ = this.store.select(UserSelectors.selectLocation);

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<RootState>,
    private readonly router: Router,
  ) {}

  private getBrowserUrl(): string {
    return `${globalThis.location.pathname}${globalThis.location.search}`;
  }

  private normalizeUrl(url?: string | null): string {
    return url || this.getBrowserUrl();
  }

  private getRootSegment(url?: string | null): string {
    return this.normalizeUrl(url).replace(/^\//, '').split(/[/?#]/)[0] ?? '';
  }

  private getVariantFromRootSegment(rootSegment?: string | null): FlockVariant {
    return rootSegment === 'breeders' ? FLOCK_VARIANT.Breeder : FLOCK_VARIANT.Layer;
  }

  private isFlockOverviewRootSegment(rootSegment?: string | null): boolean {
    return rootSegment === 'flock' || rootSegment === 'breeders';
  }

  private getLoadDataActionForUrl(url?: string | null) {
    return flockActions.loadData({
      variant: this.getVariantFromRootSegment(this.getRootSegment(url)),
    });
  }

  private isSelectRelationshipRoute(url?: string | null): boolean {
    return this.normalizeUrl(url).startsWith('/select-relationship');
  }

  private isUserNavigationInProgress(): boolean {
    return this.router.currentNavigation !== null;
  }

  private hasSelectedRelationship(customer: unknown, location: unknown): boolean {
    return !!customer && !!location;
  }

  private waitForSelectedRelationship() {
    return combineLatest([this.selectedCustomer$, this.selectedLocation$]).pipe(
      filter(([customer, location]) => this.hasSelectedRelationship(customer, location)),
      take(1),
    );
  }

  customerOrLocationChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.customerChanged, UserActions.locationChanged),
      withLatestFrom(this.currentUrl$, this.selectedCustomer$, this.selectedLocation$),
      filter(([_, __, customer, location]) => this.hasSelectedRelationship(customer, location)),
      switchMap(([_, url]) => {
        const rootSegment = this.getRootSegment(url);
        const target = rootSegment === 'select-relationship' ? '/' : rootSegment;
        if (
          target &&
          !this.isFlockOverviewRootSegment(rootSegment) &&
          !this.isUserNavigationInProgress()
        ) {
          this.router.navigate([target]).then();
        }
        return of(this.getLoadDataActionForUrl(url));
      }),
    ),
  );

  locationInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.locationInitialized),
      withLatestFrom(this.currentUrl$),
      filter(
        ([_, url]) =>
          !this.isSelectRelationshipRoute(url) &&
          !this.isFlockOverviewRootSegment(this.getRootSegment(url)),
      ),
      switchMap(([_, url]) => of(this.getLoadDataActionForUrl(url))),
    ),
  );

  flockOverviewNavigated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      map((action: RouterNavigationAction<{ url: string }>) => action.payload.routerState.url),
      map((url) => ({
        url,
        rootSegment: this.getRootSegment(url),
      })),
      filter(({ rootSegment }) => this.isFlockOverviewRootSegment(rootSegment)),
      distinctUntilChanged((previous, current) => previous.rootSegment === current.rootSegment),
      switchMap(({ rootSegment }) =>
        this.waitForSelectedRelationship().pipe(
          map(() =>
            flockActions.loadData({ variant: this.getVariantFromRootSegment(rootSegment) }),
          ),
        ),
      ),
    ),
  );
}
